import { ROLES, SCHEMES } from './schemes.js';

/**
 * Material You, seeded by the car.
 *
 * The thing that makes Material You *you* is that the palette is not chosen by the designer:
 * it is generated from one colour that belongs to the person — on a phone, their wallpaper.
 * This game has an obvious equivalent sitting in the driver dialog already, so the whole
 * interface is tinted from the car colour, and picking a different car repaints the menus.
 *
 * The schemes themselves come from Google's generator, run at build time over the eight car
 * colours (`scripts/materialSchemes.ts`); this only puts the answer on the page. The output
 * is a set of custom properties on the root, `--light-*` and `--dark-*`, which `styles.css`
 * maps onto the role names (`--primary`, `--surface-container`, …) for whichever scheme
 * applies where. The stylesheet carries the default car's values as well, so nothing here is
 * needed for the first frame to be right — only for it to change. See DECISIONS.md §62.
 */

export type SchemeColors = Record<string, string>;

/** The default car's: a colour from anywhere else — an old stored value — gets this. */
const FALLBACK = Number(Object.keys(SCHEMES)[0]);

/** The scheme for one car colour and one brightness, as `{ 'primary': '#rrggbb', … }`. */
export function schemeColors(seed: number, dark: boolean): SchemeColors {
  const entry = SCHEMES[seed] ?? SCHEMES[FALLBACK];
  const tones = (dark ? entry.dark : entry.light).split(' ');
  return Object.fromEntries(ROLES.map((role, i) => [role, tones[i]]));
}

let applied: number | null = null;

/**
 * Repaints the interface from a car colour.
 *
 * Both schemes are written every time, because which one is showing is not this module's
 * decision: the menus follow the operating system and the race is always dark, and both can
 * be on screen at once.
 */
export function applyTheme(seed: number, root: HTMLElement = document.documentElement): void {
  if (applied === seed) return;
  applied = seed;
  for (const dark of [false, true]) {
    const prefix = dark ? '--dark-' : '--light-';
    for (const [name, value] of Object.entries(schemeColors(seed, dark))) {
      root.style.setProperty(prefix + name, value);
    }
  }
  // The browser's own chrome — the address bar on a phone — follows along.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = schemeColors(seed, true)['surface-container'];
}
